<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { watch, ref, computed, onBeforeMount, onBeforeUnmount } from "vue"
import { useRoute } from "vue-router"
import type { Source } from "@types"
import {
    useRedirect,
    useFetchWrapper,
    useWorkflowEnedisMessage,
} from "@core/composables"
import { SOURCES_TYPES } from "@core/constants"
import { useGraphStore } from "@modules/data/stores/graph.store"
import { useBuildingStore } from "@core/stores/building-v2.store"
import { useI18n } from "vue-i18n"
import { EnergyUse } from "@modules/energy-manager/helpers/energyuse"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Consommation du chauffage",
            warning_message: () => {
                return `Pour afficher les graphiques, il faut au minimum un <strong>compteur de gaz</strong> et dans les <span id="redirect-link" class="redirect_link">informations du bâtiment</span>, renseigner dans le type de chauffage : <strong>une chaudière au gaz</strong> et dans le type d'eau chaude sanitaire (ECS) : <strong>un ballon d'eau chaude électrique</strong>.`
            },
            button_add_pce: "Ajouter un compteur de gaz",
            button_edit_energy_use:
                "Modifier les informations sur le chauffage et l'ECS",
            error_no_charts: "Aucun graphique disponible pour ce bâtiment.",
        },
    },
})

// ================================
// COMPOSABLES
// ================================
const route = useRoute()
const graphStore = useGraphStore()
const buildingStore = useBuildingStore()
const { redirectWrapper } = useRedirect()

const { fetch: fetchWorkflowEnedis } = useWorkflowEnedisMessage()

const { fetch: fetchInitialData, isFetching: isFetchingInitialData } =
    useFetchWrapper(async () => {
        if (!graphStore.isGraphListLoaded) {
            await graphStore.fetchGraphList()
        }
        fetchWorkflowEnedis()
    })

defineEmits(["click:source"])

// ================================
// DATA
// ================================
const energyUse = ref<EnergyUse | null>(null)

/** Modal d’ajout de compteur gaz (PCE) */
const sourceAppendModalVisible = ref(false)

/**
 * Met à jour energyUse à partir du bâtiment chargé.
 */
function updateEnergyUseFromBuilding() {
    const building = buildingStore.building
    if (!building) return
    const niveau =
        building?.infos && "niveau" in building.infos
            ? building.infos.niveau
            : "immeuble"
    const field = ["appartement", "maison_appartement", "maison"].includes(
        niveau! as string
    )
        ? "logement"
        : "batiment"
    const energyuseData = building?.infos?.[field]?.energyuse
    energyUse.value = new EnergyUse(energyuseData ?? [])
}

// ================================
// COMPUTED
// ================================
/**
 * Liste des graphiques affichage.
 * Pour chaque graph on vérifie si au moins une condition est vérifiée.
 * Pour qu'une condition soit vérifiée ces deux champs soit doivent l'etre.
 */
const getGraphBuildingList = computed(() => {
    return Object.values(
        graphStore.getGraphConfigByCategory("GRAPH_CONSO_CHAUFFAGE")
    ).map((graph) => {
        return {
            graph_id: graph.id,
            name: graph.name,
            date_from: graphStore.period[0],
            date_to: graphStore.period[1],
            freq: graphStore.freq,
        }
    })
})
/**
 * Récupère la liste des options de carte de graphiques disponibles
 */
const getCardGraphOptionList = computed(() => {
    return getGraphBuildingList.value.map((graph) => {
        return graph.graph_id
    })
})

/**
 * Récupère la liste des identifiants de graphique à afficher
 */
const getGraphIdList = computed(() => {
    return getGraphBuildingList.value
        .filter((graph) => graphStore.graphs.includes(graph.graph_id))
        .map((graph) => graph.graph_id)
})

/**
 * Indique si le bâtiment a besoin d'un compteur de gaz
 */
const isNeedGazMeter = computed(() => {
    return !buildingStore.getSources.some((source: Source) =>
        [SOURCES_TYPES.PCE, SOURCES_TYPES.REGAZ].includes(source.idtype)
    )
})

/**
 * Retourne true si les informations sur le chauffage et l'ECS sont invalides dans les équipements du bâtiment
 */
const isEnergyUseInvalid = computed(() => {
    const energyUseForm = energyUse.value?.createFormFromGraph()
    const isChauffageGaz = energyUseForm?.chauffage === "gaz"
    const isEcsNotGaz = energyUseForm?.ecs !== "gaz"
    return !isChauffageGaz || !isEcsNotGaz
})

//======================================================================//
//                         OnClick Methods                              //
//======================================================================//
/**
 * Quand l'utilisateur clique sur recharger les données de la source,
 * envoie un message à la fenêtre parent pour recharger les données.
 */
function onClickRemove(graphId: string): void {
    onUpdateGraphList(
        graphStore.graphs.filter((graph: string) => graph !== graphId)
    )
}

function onUpdateGraphList(graphs: string[]) {
    graphStore.updateGraphList(graphs)
}

// ================================
// WATCHERS
// ================================
/**
 * Surveille les changements de bâtiment et charge les données si nécessaire
 */
watch(
    () => route.params?._id_building,
    () => {
        void fetchInitialData()
    },
    { immediate: true }
)

/**
 * Une fois le bâtiment, les sources et la liste des graphiques chargés,
 * initialise energyUse et graphStore.graphs (liste optimale chauffage).
 */
watch(
    () => isFetchingInitialData.value,
    (fetching) => {
        if (
            !fetching &&
            route.params._id_building &&
            graphStore.isGraphListLoaded
        ) {
            updateEnergyUseFromBuilding()
            graphStore.updateGraphList(graphStore.getOptimalBuildingGraph)
        }
    },
    { immediate: true }
)

// ================================
// INTERACTIONS
// ================================
function onAddPce() {
    sourceAppendModalVisible.value = true
}

/**
 * Fin du parcours d’ajout PCE : même rechargement que succès.
 */
async function onSourceAppendDone(): Promise<void> {
    const id = route.params._id_building
    if (!id) return
    const idStr = Array.isArray(id) ? id[0] : id
    await buildingStore.fetchBuilding(idStr, { silent: true })
    await buildingStore.fetchBuildingSources({
        force: true,
        silent: true,
    })
    await fetchInitialData()
}
/**
 * Quand l'utilisateur clique sur modifier les informations sur le chauffage et l'ECS,
 * redirige vers la page de modification des informations du bâtiment.
 */
function onEditEnergyUse() {
    redirectWrapper("/building/:_id_building/info-general", {
        _id_building: buildingStore.building?._id,
    })
}

// ================================
// LIFECYCLE
// ================================

/**
 * Mets la valeur de fréquence par défault à raw
 */
onBeforeMount(() => {
    graphStore.freq = "daily"
})

onBeforeUnmount(() => {
    graphStore.cancelPendingGraphRequests()
})
</script>
<template>
    <div class="page page-building-conso-chauffag">
        <!-- ================================ -->
        <!-- EN-TÊTE -->
        <!-- ================================ -->
        <HeaderPageBuilding :title="t('title')" />

        <!-- ================================ -->
        <!-- ÉTAT DE CHARGEMENT -->
        <!-- ================================ -->
        <template v-if="isFetchingInitialData">
            <BaseSkeleton width="100%" height="3rem" />
            <BaseSkeleton width="100%" height="40rem" />
            <BaseSkeleton width="100%" height="40rem" />
        </template>

        <!-- ================================ -->
        <!-- CONTENU PRINCIPAL -->
        <!-- ================================ -->
        <template v-else>
            <!-- Header -->
            <DataHeader
                :hide-select-graphs="true"
                :option-graphs="getCardGraphOptionList"
                v-model:freq="graphStore.freq"
                v-model:period="graphStore.period"
                :graphs="graphStore.graphs"
                :freqs="['daily']"
                @update:graphs="onUpdateGraphList"
            />
            <!-- Message d'erreur si besoin d'ajouter un compteur de Gaz ou un capteur pour voir la page -->
            <BaseMessage
                v-if="isNeedGazMeter || isEnergyUseInvalid"
                icon="pi pi-info-circle"
                severity="warn"
            >
                <p v-html="t('warning_message')"></p>
                <div class="flex gap-2 flex-wrap">
                    <BaseButton
                        class="mt-2"
                        v-if="isNeedGazMeter"
                        size="small"
                        severity="warn"
                        @click="onAddPce"
                        :label="t('button_add_pce')"
                    />
                    <BaseButton
                        class="mt-2"
                        v-if="isEnergyUseInvalid"
                        size="small"
                        severity="warn"
                        @click="onEditEnergyUse"
                        :label="t('button_edit_energy_use')"
                    />
                </div>
            </BaseMessage>
            <!-- Charts -->
            <DataChartListItem
                class="mt-20"
                v-for="graphId in getGraphIdList"
                :key="graphId"
                :graph-id="graphId"
                type="building"
                :building="buildingStore.building"
                :sources="buildingStore.getSources"
                @click:source="$emit('click:source', $event)"
                @click:remove="onClickRemove(graphId)"
            />
            <BaseMessage
                v-if="getGraphIdList.length === 0"
                icon="pi pi-info-circle"
                severity="info"
                class="page-building-conso-chauffag__message-empty w-fit mx-auto mt-20"
            >
                {{ t("error_no_charts") }}
            </BaseMessage>
        </template>

        <SourceAppendDialog
            v-model:visible="sourceAppendModalVisible"
            from-state="pce"
            :allowed-source-types="['pce']"
            :building-id="
                (route.params._id_building as string | undefined) ?? undefined
            "
            :address="buildingStore.building?.address ?? null"
            @done="onSourceAppendDone"
        />
    </div>
</template>
<style scoped>
.page-building-conso-chauffag {
    & .page-building-conso-chauffag__message-empty-content {
        width: fit-content;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: center;
        gap: 1.5rem;
    }
    & .page-building-conso-chauffag__workflow-enedis {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }
}
</style>
